import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/context/ToastContext';
import { ICFProgressAPI } from '@/services/api/icfProgressAPI';
import { authAPI } from '@/services/api';
import { isSchoolAdmin, isSuperAdmin } from '@/utils/roleUtils';
import { confirmDialog, showDialog } from '@/utils/dialogs';

export default function DataManagementScreen() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [retention, setRetention] = useState<string>('365');
  const [saving, setSaving] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  const load = useCallback(async () => {
    try {
      // Role guard: only super admins or school admins
      const user = await authAPI.getCurrentUser();
      if (!(isSuperAdmin(user as any) || isSchoolAdmin(user as any))) {
        setUnauthorized(true);
        return;
      }
      const progress = await ICFProgressAPI.getProgress();
      setRetention(String(progress.data_retention_days || 365));
    } catch (e: any) {
      showDialog(t('common.error'), e?.message || t('icfProgress.dataManagement.loadError'));
    }
  }, [t]);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    try {
      setSaving(true);
      const days = parseInt(retention || '365', 10);
      if (!Number.isFinite(days) || days < 1 || days > 2555) {
        showDialog(t('common.error'), t('icfProgress.dataManagement.retentionInvalid'));
        return;
      }
      await ICFProgressAPI.updatePreferences({ data_retention_days: days });
      showToast(t('icfProgress.dataManagement.saveSuccess'), 'success');
    } catch (e: any) {
      showDialog(t('common.error'), e?.message || t('icfProgress.dataManagement.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const purge = async () => {
    const confirmed = await confirmDialog(
      t('icfProgress.dataManagement.purgeConfirmTitle'),
      t('icfProgress.dataManagement.purgeConfirmBody'),
      t('icfProgress.dataManagement.purgeConfirmAction'),
      t('common.cancel'),
    );
    if (!confirmed) return;
    try {
      await ICFProgressAPI.purgeProgressData();
      await load();
      showToast(t('icfProgress.dataManagement.purgeSuccess'), 'success');
    } catch (e: any) {
      showDialog(t('common.error'), e?.message || t('icfProgress.dataManagement.purgeError'));
    }
  };

  if (unauthorized) {
    return (
      <View style={styles.center} testID="data_management_unauthorized">
        <Text selectable style={styles.errorText}>{t('common.accessDenied')}</Text>
      </View>
    );
  }

  return (
    <ScrollView
      testID="data_management_screen"
      style={styles.screen}
      contentContainerStyle={styles.content}
    >
      <View style={styles.panel}>
        <Text selectable style={styles.title}>{t('icfProgress.dataManagement.title')}</Text>
        <Text selectable style={styles.desc}>{t('icfProgress.dataManagement.subtitle')}</Text>
        <View style={styles.row}>
          <Text selectable style={styles.label}>{t('icfProgress.dataManagement.retentionLabel')}</Text>
          <TextInput
            testID="data_management_retention_input"
            accessibilityLabel={t('icfProgress.dataManagement.retentionLabel')}
            style={styles.input}
            keyboardType="numeric"
            value={retention}
            onChangeText={setRetention}
          />
        </View>
        <Pressable
          testID="data_management_save"
          accessibilityRole="button"
          onPress={() => void save()}
          disabled={saving}
          style={({ pressed }) => [styles.button, (pressed || saving) && styles.disabled]}
        >
          <Text selectable style={styles.buttonText}>
            {saving ? t('common.saving') : t('common.save')}
          </Text>
        </Pressable>
      </View>

      <View style={styles.panel}>
        <Text selectable style={styles.title}>{t('icfProgress.dataManagement.purgeTitle')}</Text>
        <Text selectable style={styles.desc}>{t('icfProgress.dataManagement.purgeSubtitle')}</Text>
        <Pressable
          testID="data_management_purge"
          accessibilityRole="button"
          onPress={() => void purge()}
          style={({ pressed }) => [styles.button, styles.dangerButton, pressed && styles.disabled]}
        >
          <Text selectable style={[styles.buttonText, styles.dangerButtonText]}>
            {t('icfProgress.dataManagement.purgeAction')}
          </Text>
        </Pressable>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: reviewDesk.canvas },
  content: { width: '100%', maxWidth: 900, alignSelf: 'center', padding: 20, gap: 16 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.canvas, padding: 24 },
  panel: { width: '100%', backgroundColor: reviewDesk.card, borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, padding: 18 },
  title: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800' },
  desc: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19, marginTop: 6 },
  row: { marginTop: 12, gap: 6 },
  label: { color: reviewDesk.ink, fontSize: 14, fontWeight: '600' },
  input: { borderWidth: 1, borderColor: reviewDesk.lineStrong, color: reviewDesk.ink, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  button: { minHeight: 44, marginTop: 12, borderRadius: 10, paddingVertical: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.accentSoft, borderWidth: 1, borderColor: reviewDesk.lineStrong },
  buttonText: { color: reviewDesk.accent, fontSize: 15, fontWeight: '700' },
  dangerButton: { backgroundColor: '#FEE4E2', borderColor: '#FDA29B' },
  dangerButtonText: { color: '#B42318' },
  disabled: { opacity: 0.6 },
  errorText: { color: '#B42318', textAlign: 'center', lineHeight: 22 },
});
